'use client';
import React, { useState } from 'react';
import { useModel } from '@/context/ModelContext';

import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  Factory,
  TrendingUp,
  Package,
  Building2,
  Wrench,
  CreditCard,
  Settings2,
  RefreshCw,
  Info,
} from 'lucide-react';
import CapacitySection from './CapacitySection';
import RevenueSection from './RevenueSection';
import VariableCostsSection from './VariableCostsSection';
import FixedCostsSection from './FixedCostsSection';
import CapexSection from './CapexSection';
import FinancingSection from './FinancingSection';
import OtherSection from './OtherSection';
import DerivedOutputsPanel from './DerivedOutputsPanel';

interface Section {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

export default function AssumptionsContent() {
  const { resetToDefaults } = useModel();
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(['capacity', 'revenue', 'variable'])
  );

  function toggleSection(id: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function expandAll() {
    setOpenSections(
      new Set(['capacity', 'revenue', 'variable', 'fixed', 'capex', 'financing', 'other'])
    );
  }

  function collapseAll() {
    setOpenSections(new Set());
  }

  function handleReset() {
    resetToDefaults();
    toast.success('Assumptions reset to base case defaults', { duration: 3000 });
  }

  const sections: Section[] = [
    {
      id: 'capacity',
      label: 'A. Capacity & Operations',
      description: 'TPH, operating hours, utilisation rates',
      icon: <Factory size={16} />,
      component: <CapacitySection />,
    },
    {
      id: 'revenue',
      label: 'B. Revenue',
      description: 'Selling price, escalation, carbon credits',
      icon: <TrendingUp size={16} />,
      component: <RevenueSection />,
    },
    {
      id: 'variable',
      label: 'C. Variable Costs',
      description: '₹/tonne — RM, power, labour, maintenance, packaging',
      icon: <Package size={16} />,
      component: <VariableCostsSection />,
    },
    {
      id: 'fixed',
      label: 'D. Fixed Costs',
      description: 'Annual fixed costs — land lease, salaries, insurance',
      icon: <Building2 size={16} />,
      component: <FixedCostsSection />,
    },
    {
      id: 'capex',
      label: 'E. CAPEX (Lean 5 TPH)',
      description: 'Machinery, civil, electrical, contingency, working capital',
      icon: <Wrench size={16} />,
      component: <CapexSection />,
    },
    {
      id: 'financing',
      label: 'F. Financing',
      description: 'Equity, debt, interest rate, tenure, depreciation',
      icon: <CreditCard size={16} />,
      component: <FinancingSection />,
    },
    {
      id: 'other',
      label: 'G. Other Parameters',
      description: 'Discount rate (WACC), fixed cost inflation',
      icon: <Settings2 size={16} />,
      component: <OtherSection />,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Assumptions & Inputs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Single source of truth — all yellow fields are editable. Changes propagate instantly to
            all projections.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={expandAll} className="btn-secondary text-xs py-1.5 px-3">
            Expand All
          </button>
          <button onClick={collapseAll} className="btn-secondary text-xs py-1.5 px-3">
            Collapse All
          </button>
          <button
            onClick={handleReset}
            className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <RefreshCw size={12} />
            Reset Defaults
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
          <span className="text-muted-foreground">Editable Input</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-green-50 border border-green-200" />
          <span className="text-muted-foreground">Calculated (read-only)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="confidence-high">High</span>
          <span className="text-muted-foreground">/ </span>
          <span className="confidence-medium">Medium</span>
          <span className="text-muted-foreground">Confidence</span>
        </div>
      </div>

      {/* Derived Outputs Panel — always visible */}
      <DerivedOutputsPanel />

      {/* Accordion sections */}
      <div className="space-y-3">
        {sections.map((section) => (
          <div
            key={`section-${section.id}`}
            className="bg-card border border-border rounded-xl overflow-hidden"
          >
            <button
              className="accordion-header w-full text-left"
              onClick={() => toggleSection(section.id)}
              aria-expanded={openSections.has(section.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  {section.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{section.label}</p>
                  <p className="text-xs text-muted-foreground">{section.description}</p>
                </div>
              </div>
              <div className="flex-shrink-0 text-muted-foreground">
                {openSections.has(section.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </button>

            {openSections.has(section.id) && (
              <div className="border-t border-border p-5">{section.component}</div>
            )}
          </div>
        ))}
      </div>

      {/* Model notes */}
      <div className="bg-muted/50 border border-border rounded-xl p-4">
        <div className="flex items-start gap-2">
          <Info size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-foreground mb-1">
              Model Limitations & Disclaimer
            </p>
            <ul className="space-y-1">
              {[
                '10-year explicit forecast only — no terminal value in NPV/IRR',
                'Debt amortisation is simplified (not full EMI schedule with exact bank terms)',
                'No monthly cash-flow seasonality for feedstock',
                'Carbon credits and MNRE subsidies excluded from Base Case',
                'Working capital modelled as initial lump sum only — ongoing WC changes not modelled monthly',
                'Power remains full grid; solar can be added as sensitivity / Phase-2',
              ].map((note, idx) => (
                <li
                  key={`limitation-${idx}`}
                  className="text-xs text-muted-foreground flex items-start gap-1.5"
                >
                  <span className="text-muted-foreground mt-0.5">•</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
